import { useMemo, useState, type ReactNode, type Ref } from 'react';
import { Icon } from './Icon.js';
import { IconButton } from './IconButton.js';
import { Menu } from './Menu.js';
import { Tabs } from './Tabs.js';
import './NotificationTray.css';

export interface TrayNotification {
  id: string;
  title: string;
  detail?: string;
  /** Already-formatted relative time — formatting is M30's job, not this one's. */
  meta?: string;
  read?: boolean;
  /**
   * Which tab it belongs under. The prototype splits "needs you" from
   * "everything", which is the split that matters in a product where some
   * notifications are decisions and others are just events.
   */
  needsYou?: boolean;
  onSelect?: () => void;
}

export interface NotificationTrayProps {
  /**
   * ⚠️ **`null` means "we could not read them"** — distinct from `[]`, which means there are none.
   *
   * The two used to share a rendering: the shell mapped an unreadable plane to an empty list and the
   * tray said *"No notifications yet"*, which is a screen stating something it does not know. A
   * client whose plane is down is exactly the client most likely to have something waiting.
   */
  notifications: TrayNotification[] | null;
  /** Rendered in the footer band — the weekly-digest line in the prototype. */
  footer?: ReactNode;
  onMarkAllRead?: () => void;
}

/**
 * The notification tray (design-system catalogue, Tier 2) — bell, unread count,
 * and a filtered panel.
 *
 * Absent from the build entirely before this (audit S-5), despite 08 §2 listing it
 * as a global element and F14/M22 owning the events it renders. This is the
 * *surface*: it takes notifications and renders them. It deliberately does not
 * fetch, mark-read server-side, or subscribe — M22 owns the store, and a tray
 * that invented its own state would end up disagreeing with it.
 */
export function NotificationTray({
  notifications,
  footer,
  onMarkAllRead,
}: NotificationTrayProps) {
  const [tab, setTab] = useState('needs_you');

  /* ⚠️ `null` is not zero unread — the badge is hidden entirely rather than showing `0`, because a
     `0` on the bell is a positive claim that nothing is waiting. */
  const unread = notifications === null ? null : notifications.filter((n) => !n.read).length;
  const visible = useMemo(
    () =>
      notifications === null
        ? []
        : tab === 'needs_you'
          ? notifications.filter((n) => n.needsYou)
          : notifications,
    [notifications, tab]
  );

  return (
    <Menu
      label="Notifications"
      align="end"
      width={360}
      trigger={({ ref, ...triggerProps }) => (
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <IconButton
            {...triggerProps}
            ref={ref as Ref<HTMLButtonElement>}
            size="md"
            variant="ghost"
            icon={<Icon name="bell" size={19} />}
            // The count goes in the accessible name in words. The visible badge is
            // aria-hidden, so this is the only place it carries meaning — the same
            // rule the nav badges follow after B-59.
            label={
              unread === null
                ? 'Notifications, unread count unavailable'
                : unread > 0
                  ? `Notifications, ${unread} unread`
                  : 'Notifications, none unread'
            }
          />
          {unread !== null && unread > 0 ? (
            <span className="dpr-tray__count" aria-hidden="true">
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </span>
      )}
    >
      <div className="dpr-tray__panel">
        <div className="dpr-tray__head">
          <Tabs
            label="Filter notifications"
            as="group"
            value={tab}
            onChange={setTab}
            items={[
              { id: 'needs_you', label: 'Needs you' },
              { id: 'all', label: 'All' },
            ]}
          />
        </div>

        {notifications === null ? (
          // ⚠️ The not-known state (B-164). It must not read as calm: the whole point is that we
          // cannot say whether anything is waiting, and the reader has to know to look again.
          <p className="dpr-tray__empty dpr-tray__empty--unknown">
            We could not load your notifications just now. This is not the same as having none —
            try again shortly.
          </p>
        ) : visible.length === 0 ? (
          // A named empty state, never a blank panel — patterns.md's mandatory
          // state set applies to a popover as much as to a screen.
          <p className="dpr-tray__empty">
            {tab === 'needs_you'
              ? 'Nothing is waiting on you.'
              : 'No notifications yet.'}
          </p>
        ) : (
          <ul className="dpr-tray__list">
            {visible.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={[
                    'dpr-tray__item',
                    n.read ? null : 'dpr-tray__item--unread',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={n.onSelect}
                  aria-label={`${n.read ? '' : 'Unread. '}${n.title}${n.meta ? `, ${n.meta}` : ''}`}
                >
                  <span
                    className={['dpr-tray__dot', n.read ? 'dpr-tray__dot--read' : null]
                      .filter(Boolean)
                      .join(' ')}
                    aria-hidden="true"
                  />
                  <span className="dpr-tray__body">
                    <span className="dpr-tray__title">{n.title}</span>
                    {n.detail ? <span className="dpr-tray__detail">{n.detail}</span> : null}
                    {n.meta ? <span className="dpr-tray__meta">{n.meta}</span> : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {footer || onMarkAllRead ? (
          <div className="dpr-tray__foot">
            {onMarkAllRead && unread !== null && unread > 0 ? (
              <button type="button" className="dpr-tray__item" onClick={onMarkAllRead}>
                Mark all as read
              </button>
            ) : null}
            {footer}
          </div>
        ) : null}
      </div>
    </Menu>
  );
}
